import React, { MouseEvent, useState } from 'react';
import moment from 'moment';
import _ from 'lodash';
import { Redirect } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import styled from 'styled-components';
import { lighten } from 'polished';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import DuplicateIcon from '@material-ui/icons/ControlPointDuplicate';
import ShareIcon from '@material-ui/icons/Link';
import DeleteIcon from '@material-ui/icons/Delete';
import ExportIcon from '@material-ui/icons/GetApp';
import { AxiosError, AxiosResponse } from 'axios';
//
import { TActionBarProps } from 'components/action-bar';
import UrlShare from 'components/url-share';
import Menu from 'components/menu';
import MenuItem from 'components/menu-item';
import colors from 'theme/colors';
import labels from 'constants/labels';
import messages from 'constants/messages';
import ModalBasic from 'components/modal-provider/modal-basic';
import { ModalButton, ModalCancelButton, ModalDeleteButton } from 'components/modal-provider/modal';
import SimpleButton from 'components/buttons/simple';
import { IDeal } from 'store/deals/types';
import routes from 'router/routes';
import { getAccessToken } from 'lib/auth';
import apiService from 'services/api';
import apiRoutes from 'services/api/routes';
import { getAxiosErrorMessage, handleError } from 'lib/helpers/error-handler';
import { toApi } from 'lib/helpers/deal-formatters';
import { EForecastEvaluationType, EEvaluationUnit } from 'store/deal-evaluation/types';
import ForecastToggle from 'components/forecast-toggle';
import UnitToggle from 'components/unit-toggle';
import CostFormulaToggle from 'components/cost-formula-toggle';

const { error, primary, white } = colors;

const Container = styled.div`
  background-color: ${white};
  box-shadow: 0 1px 3px 0 rgba(32, 33, 36, 0.3);
  display: flex;
  height: 60px;
  justify-content: space-between;
  align-items: center;
  padding: 0px 20px;
  position: sticky;
  top: 59px;
  z-index: 2;

  button: {
    height: 40px;
  }
`;

const SaveButton = styled(SimpleButton)`
  margin-right: 8px;
`;

const DeleteButton = withStyles({
  root: {
    color: error,
  },
})(MenuItem);

const OptionsButton = styled(SimpleButton)`
  min-width: 40px;
  padding: 0px 12px;
`;

const MenuItemText = styled.span`
  margin-left: 16px;
`;

const colorGradient = 0.2;

const ExportButton = styled.button`
  color: ${primary};
  margin-right: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border: none;
  background-color: transparent;
  transition: color 0.2s ease;

  :hover {
    color: ${lighten(colorGradient, primary)};
    cursor: pointer;
  }

  :focus {
    outline: none;
  }
`;

const ButtonText = styled.p`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  padding-right: 2px;
`;

const Left = styled.div`
  display: flex;

  & > div {
    margin-right: 16px;
  }
`;

const Right = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export function downloadFile(
  dealName: string,
  selectedForecastType: EForecastEvaluationType,
  selectedUnitType: EEvaluationUnit,
  data: BlobPart
) {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute(
    'download',
    `${dealName.trim().replace(/ /g, '_')}_${selectedForecastType}_${selectedUnitType}_${new Date().toISOString()}.xlsx`
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function processForDuplicate(dealToDuplicate: IDeal) {
  // check for unprocessed moment dates
  const dealToDuplicateProcessed = {
    ...dealToDuplicate,
    products: dealToDuplicate.products
      ? dealToDuplicate.products.map(product => ({
          ...product,
          startDate: moment.isMoment(product.startDate) ? moment(product.startDate).toDate() : product.startDate,
          endDate: moment.isMoment(product.endDate) ? moment(product.endDate).toDate() : product.endDate,
        }))
      : null,
  };

  return (
    <Redirect
      to={{
        pathname: routes.dealCreate,
        state: {
          dealToDuplicate: dealToDuplicateProcessed,
        },
      }}
    />
  );
}

function handleExport(
  dealInEvaluation: IDeal | undefined | null,
  handleExportError: Function,
  selectedForecastType: EForecastEvaluationType,
  selectedUnitType: EEvaluationUnit
) {
  if (!dealInEvaluation) {
    handleExportError(messages.errors.default);
    return;
  }

  apiService
    .post(apiRoutes.dealExcelEvaluation, toApi(dealInEvaluation), {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
      params: {
        forecast: selectedForecastType,
        outputUnit: selectedUnitType,
      },
    })
    .then(({ data }: AxiosResponse) => {
      if (data.error) {
        throw new Error(data.error.message);
      }
      downloadFile(dealInEvaluation.name, selectedForecastType, selectedUnitType, data);
    })
    .catch((exportError: AxiosError) => {
      const errorMessage = getAxiosErrorMessage(exportError);
      handleExportError(errorMessage);
    });
}

export default ({
  canSaveDeal,
  clearModalContent,
  dealEdit,
  dealInEvaluation,
  deleteDeal,
  isSidebarVisible,
  saveDeal,
  setModalContent,
  showExportButton,
  selectedForecastType,
  selectedUnitType,
  isLoggedUserSameForDeal,
}: TActionBarProps) => {
  const [anchorOptionsButton, setAnchorOptionsButton] = useState<null | HTMLElement>(null);
  const [dealToDuplicate, setDealToDuplicate] = useState<null | IDeal>(null);
  const statusToDuplicate = '';

  const { id } = dealEdit;

  function handleCloseOptions() {
    setAnchorOptionsButton(null);
  }

  function handleDelete() {
    handleCloseOptions();
    clearModalContent();

    const handleModalDeleteButton = () => {
      if (id) {
        deleteDeal(id);
        clearModalContent();
      }
    };

    const deleteButtons = (
      <>
        <ModalCancelButton onClick={clearModalContent}>{labels.button.cancel}</ModalCancelButton>
        <ModalDeleteButton id="action-bar-modal-delete-button" onClick={handleModalDeleteButton}>
          {labels.button.deleteConfirmation}
        </ModalDeleteButton>
      </>
    );

    const modalContent = <p>{messages.warnings.deleteDealConfirmation}</p>;

    const modal = (
      <ModalBasic
        title={labels.titles.modals.deleteDeal}
        content={modalContent}
        onClose={clearModalContent}
        buttons={deleteButtons}
      />
    );

    setModalContent(modal);
  }

  function handleOptionsClick(event: MouseEvent<HTMLButtonElement>) {
    setAnchorOptionsButton(event.currentTarget);
  }

  function handleDuplicate() {
    const deal = _.cloneDeep(dealEdit);
    delete deal.id;
    deal.status = statusToDuplicate;
    setDealToDuplicate(deal);
    setAnchorOptionsButton(null);
    clearModalContent();
  }

  function showDuplicateModal() {
    setAnchorOptionsButton(null);
    const modalContent = (
      <>
        <p>{messages.warnings.duplicateDealConfirmation}</p>
        <br />
      </>
    );

    const modalButtons = (
      <>
        <ModalCancelButton onClick={clearModalContent}>{labels.button.close}</ModalCancelButton>
        <ModalButton onClick={handleDuplicate}>{labels.button.duplicate}</ModalButton>
      </>
    );

    const modal = (
      <ModalBasic
        title={labels.titles.modals.duplicateDeal}
        content={modalContent}
        onClose={clearModalContent}
        buttons={modalButtons}
      />
    );

    setModalContent(modal);
  }

  function handleShare() {
    setAnchorOptionsButton(null);
    const modalContent = (
      <>
        <p>{messages.info.shareDeal}</p>
        <UrlShare url={`${window.location.href}`} />
        <br />
      </>
    );

    const modalButton = <ModalButton onClick={clearModalContent}>{labels.button.close}</ModalButton>;

    const modal = (
      <ModalBasic
        title={labels.titles.modals.shareDeal}
        content={modalContent}
        onClose={clearModalContent}
        buttons={modalButton}
      />
    );

    setModalContent(modal);
  }

  function getSaveButtonAttributes() {
    return {
      disabled: !canSaveDeal,
      onClick: saveDeal,
      id: 'action-bar-save-button',
    };
  }

  if (!isSidebarVisible) {
    return null;
  }

  if (dealToDuplicate) {
    return processForDuplicate(dealToDuplicate);
  }

  return (
    <Container>
      <Left>
        <ForecastToggle showTitle={false} />
        <UnitToggle showTitle={false} />
        <CostFormulaToggle showTitle={false} />
      </Left>
      <Right>
        {showExportButton ? (
          <ExportButton
            id="action-bar-export-button"
            onClick={() => handleExport(dealInEvaluation, handleError, selectedForecastType, selectedUnitType)}
          >
            <ButtonText>Export</ButtonText>
            <ExportIcon fontSize="small" />
          </ExportButton>
        ) : null}
        <SaveButton {...getSaveButtonAttributes()}>Save Deal</SaveButton>
        <>
          <OptionsButton id="action-bar-menu-button" onClick={handleOptionsClick} disabled={!id}>
            <ArrowDropDownIcon />
          </OptionsButton>
          <Menu
            id="action-bar-menu"
            anchorEl={anchorOptionsButton}
            keepMounted
            open={Boolean(anchorOptionsButton)}
            onClose={handleCloseOptions}
          >
            <MenuItem id="action-bar-duplicate-button" onClick={showDuplicateModal}>
              <DuplicateIcon />
              <MenuItemText>Duplicate</MenuItemText>
            </MenuItem>
            <MenuItem id="active-bar-share-button" onClick={handleShare}>
              <ShareIcon />
              <MenuItemText>Share</MenuItemText>
            </MenuItem>
            <DeleteButton id="action-bar-delete-button" onClick={handleDelete} disabled={!isLoggedUserSameForDeal}>
              <DeleteIcon />
              <MenuItemText>Delete</MenuItemText>
            </DeleteButton>
          </Menu>
        </>
      </Right>
    </Container>
  );
};
